<template>
  <div class="heir-input">
    <!-- Tier 1: Primary Heirs -->
    <div class="heir-section">
      <h4 class="section-label">{{ $t('calculator.tier1') }}</h4>
      <div class="heir-list">
        <div
          v-for="heir in availableHeirs.tier1"
          :key="heir.category"
          class="heir-row"
        >
          <span class="heir-label">{{ $t(heir.labelKey) }}</span>
          <div class="heir-control">
            <!-- Toggle (0 or 1) -->
            <template v-if="heir.inputType === 'toggle'">
              <button
                type="button"
                class="toggle-btn"
                :class="{ active: heirCounts[heir.category] }"
                @click="toggleHeir(heir.category)"
              >
                {{ heirCounts[heir.category] ? $t('common.yes') : $t('common.no') }}
              </button>
            </template>
            <!-- Counter (0..N) -->
            <template v-else>
              <button
                type="button"
                class="counter-btn"
                :disabled="!heirCounts[heir.category]"
                @click="decrementHeir(heir.category)"
              >
                -
              </button>
              <span class="counter-value">{{ heirCounts[heir.category] || 0 }}</span>
              <button
                type="button"
                class="counter-btn"
                :disabled="heir.max !== null && (heirCounts[heir.category] || 0) >= heir.max"
                @click="incrementHeir(heir.category, heir.max)"
              >
                +
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Tier 2: Extended Family (Expandable) -->
    <div class="heir-section">
      <h4 class="section-label">{{ $t('calculator.tier2') }}</h4>
      <div
        v-for="group in availableHeirs.tier2"
        :key="group.groupKey"
        class="heir-group"
      >
        <button
          type="button"
          class="group-toggle"
          @click="toggleGroup(group.groupKey)"
        >
          <span class="group-name">{{ $t(group.labelKey) }}</span>
          <span class="group-count" v-if="getGroupCount(group) > 0">
            {{ getGroupCount(group) }}
          </span>
          <svg
            class="chevron"
            :class="{ expanded: expandedGroups[group.groupKey] }"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div v-if="expandedGroups[group.groupKey]" class="group-heirs">
          <div
            v-for="heir in group.heirs"
            :key="heir.category"
            class="heir-row"
          >
            <span class="heir-label">{{ $t(heir.labelKey) }}</span>
            <div class="heir-control">
              <template v-if="heir.inputType === 'toggle'">
                <button
                  type="button"
                  class="toggle-btn"
                  :class="{ active: heirCounts[heir.category] }"
                  @click="toggleHeir(heir.category)"
                >
                  {{ heirCounts[heir.category] ? $t('common.yes') : $t('common.no') }}
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="counter-btn"
                  :disabled="!heirCounts[heir.category]"
                  @click="decrementHeir(heir.category)"
                >
                  -
                </button>
                <span class="counter-value">{{ heirCounts[heir.category] || 0 }}</span>
                <button
                  type="button"
                  class="counter-btn"
                  :disabled="heir.max !== null && (heirCounts[heir.category] || 0) >= heir.max"
                  @click="incrementHeir(heir.category, heir.max)"
                >
                  +
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive } from 'vue'

const props = defineProps({
  availableHeirs: { type: Object, required: true },
  heirCounts: { type: Object, required: true },
})

const emit = defineEmits(['increment', 'decrement', 'toggle'])

const expandedGroups = reactive({})

const toggleGroup = (groupKey) => {
  expandedGroups[groupKey] = !expandedGroups[groupKey]
}

const getGroupCount = (group) => {
  return group.heirs.reduce((sum, heir) => sum + (props.heirCounts[heir.category] || 0), 0)
}

const incrementHeir = (category, max) => {
  emit('increment', category, max)
}

const decrementHeir = (category) => {
  emit('decrement', category)
}

const toggleHeir = (category) => {
  emit('toggle', category)
}
</script>

<style scoped>
.heir-section {
  margin-bottom: var(--space-6);
}

.section-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 var(--space-3) 0;
}

.heir-list {
  display: flex;
  flex-direction: column;
}

.heir-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--color-gray-100);
}

.heir-row:last-child {
  border-bottom: none;
}

.heir-label {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

.heir-control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  direction: ltr; /* keep [-] 0 [+] order in RTL — numeric controls shouldn't reverse */
}

.counter-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  background: var(--color-white);
  color: var(--color-charcoal);
  font-size: var(--font-size-lg);
  cursor: pointer;
  transition: all 0.15s ease;
}

.counter-btn:hover:not(:disabled) {
  border-color: var(--color-blue);
  color: var(--color-blue);
}

.counter-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.counter-value {
  min-width: 28px;
  text-align: center;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
}

.toggle-btn {
  padding: var(--space-1) var(--space-4);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-full);
  background: var(--color-white);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: all 0.15s ease;
}

.toggle-btn.active {
  background: var(--color-blue);
  border-color: var(--color-blue);
  color: var(--color-white);
}

/* Expandable Groups */
.heir-group {
  margin-bottom: var(--space-2);
}

.group-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-2);
  border: none;
  border-radius: var(--radius-md);
  background: var(--color-gray-50);
  cursor: pointer;
  transition: background 0.15s ease;
}

.group-toggle:hover {
  background: var(--color-gray-100);
}

.group-name {
  flex: 1;
  text-align: start;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.group-count {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  background: var(--color-blue);
  color: var(--color-white);
  padding: 2px 8px;
  border-radius: var(--radius-full);
}

.chevron {
  color: var(--color-gray-400);
  transition: transform 0.2s ease;
}

.chevron.expanded {
  transform: rotate(180deg);
}

.group-heirs {
  padding-inline-start: var(--space-4);
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
